/**
 * <rz-carousel>: native horizontal scroll-snap track with prev/next buttons.
 * Swipe, trackpad and keyboard scrolling come from the browser; this only wires the buttons.
 *
 *   <rz-carousel class="carousel">
 *     <div class="carousel__track" data-track tabindex="0">
 *       <div class="carousel__slide">...</div>
 *     </div>
 *     <button data-prev aria-label="Previous">‹</button>
 *     <button data-next aria-label="Next">›</button>
 *   </rz-carousel>
 */
if (!customElements.get('rz-carousel')) {
  customElements.define(
    'rz-carousel',
    class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        this.prev = this.querySelector('[data-prev]');
        this.next = this.querySelector('[data-next]');
        if (!this.track) return;

        this.prev?.addEventListener('click', () => this.go(-1));
        this.next?.addEventListener('click', () => this.go(1));
        this.track.addEventListener('scroll', () => this.update(), { passive: true });
        // The observer's first callback runs after layout, so no update() here (it would force a reflow at load).
        new ResizeObserver(() => this.update()).observe(this.track);
        this.deferSlides();
      }

      // Slides after the first view wait in <template data-deferred-slides> (snippets/product-carousel.liquid).
      // They go live on the first pointer/focus/touch on the carousel (mobile swipes, no arrows), the first scroll
      // of the track, when the browser is idle after load, or at once in the theme editor.
      deferSlides() {
        if (!this.querySelector('template[data-deferred-slides]')) return;
        this.revealSlides = this.revealSlides.bind(this);
        if (window.Shopify?.designMode || this.matches(':hover, :focus-within')) {
          this.revealSlides();
          return;
        }
        ['pointerenter', 'focusin', 'touchstart'].forEach((type) =>
          this.addEventListener(type, this.revealSlides, { once: true, passive: true })
        );
        this.track.addEventListener('scroll', this.revealSlides, { once: true, passive: true });
        const whenIdle = () =>
          'requestIdleCallback' in window
            ? requestIdleCallback(this.revealSlides, { timeout: 5000 })
            : setTimeout(this.revealSlides, 4000);
        document.readyState === 'complete' ? whenIdle() : window.addEventListener('load', whenIdle, { once: true });
      }

      // Runs once (later calls find no template), then recomputes the arrows for the longer track.
      revealSlides() {
        const template = this.querySelector('template[data-deferred-slides]');
        if (!template) return;
        template.replaceWith(template.content);
        requestAnimationFrame(() => this.update());
      }

      // Moves one visible "page" of slides.
      go(direction) {
        const rtl = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
        this.track.scrollBy({ left: direction * rtl * this.track.clientWidth, behavior: 'smooth' });
      }

      update() {
        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        const start = Math.abs(scrollLeft) < 2;
        const end = Math.abs(scrollLeft) + clientWidth >= scrollWidth - 2;
        if (this.prev) this.prev.disabled = start;
        if (this.next) this.next.disabled = end;
        this.toggleAttribute('data-scrollable', !(start && end));
      }
    }
  );
}
